import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import operatorApi from '../utils/operatorApi';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

const STATUS_COLORS = { working:'#10B981', busy:'#F59E0B', broken:'#EF4444', unknown:'#475569' };

export default function OperatorDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimName, setClaimName] = useState('');
  const [claimNote, setClaimNote] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    operatorApi.get('/operator/dashboard')
      .then(r => setData(r.data))
      .catch(() => { toast.error('Please log in'); navigate('/operator'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submitClaim = async (e) => {
    e.preventDefault();
    try {
      const res = await operatorApi.post('/operator/claim-request', { claimed_name: claimName, note: claimNote });
      toast.success(res.data.message);
      setClaimName(''); setClaimNote('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const confirmStatus = async (chargerId, status) => {
    try {
      await operatorApi.patch(`/operator/chargers/${chargerId}`, { status });
      toast.success('Status officially confirmed');
      load();
    } catch (_) { toast.error('Failed'); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'var(--teal)' }}>Loading dashboard…</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', paddingTop: 56 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px 60px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.4px' }}>
              {data.operator.operator_name}
            </h2>
            <div style={{ marginTop: 6 }}>
              <span style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: data.operator.plan_status === 'active' ? '#10B98122' : '#EF444422',
                color: data.operator.plan_status === 'active' ? '#10B981' : '#EF4444',
                border: `1px solid ${data.operator.plan_status === 'active' ? '#10B98144' : '#EF444444'}`,
              }}>
                {data.operator.plan === 'none' ? 'Free plan' : `${data.operator.plan} · ${data.operator.plan_status}`}
              </span>
            </div>
          </div>
          <button onClick={() => { localStorage.removeItem('evapp_operator_token'); navigate('/operator'); }} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-2)',
            background: 'none', color: 'var(--text-3)', fontSize: 13, cursor: 'pointer',
          }}>
            Log out
          </button>
        </div>

        {data.chargers.length === 0 ? (
          /* No chargers — show claim form */
          <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>🔗 Claim your chargers</h3>
            <p style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.6 }}>
              Your chargers may already be on the map via Open Charge Map data. Submit your operator name as it appears and we'll link them to your account.
            </p>
            <form onSubmit={submitClaim} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                placeholder="Operator name on map (e.g. 'Fastned')"
                required value={claimName}
                onChange={e => setClaimName(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--navy)', border: '1px solid var(--border-2)', color: 'var(--text-1)', fontSize: 14 }}
              />
              <textarea
                placeholder="Optional: proof of ownership, website, contact email…"
                value={claimNote}
                onChange={e => setClaimNote(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--navy)', border: '1px solid var(--border-2)', color: 'var(--text-1)', fontSize: 14, minHeight: 80, fontFamily: 'Inter, sans-serif', resize: 'none' }}
              />
              <button type="submit" style={{
                padding: '12px 0', borderRadius: 10, border: 'none',
                background: 'var(--teal)', color: 'var(--navy)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>
                Submit Claim
              </button>
            </form>
          </div>
        ) : (
          <>
            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 28 }}>
              {data.summary.map(s => (
                <div key={s.status} style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{s.status}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: STATUS_COLORS[s.status] || 'var(--text-1)', fontFamily: 'JetBrains Mono, monospace' }}>{s.count}</div>
                </div>
              ))}
            </div>

            {/* Charger list */}
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Your Fleet</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.chargers.map(c => (
                <div key={c.id} style={{
                  background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12,
                  padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3, display: 'flex', gap: 10 }}>
                      <span>📍 {c.city}, {c.country}</span>
                      <span>Reliability {c.reliability_score}/10</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <StatusBadge status={c.status} size="sm" />
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>Confirm:</span>
                    {['working','busy','broken'].map(s => (
                      <button key={s} onClick={() => confirmStatus(c.id, s)} style={{
                        padding: '4px 10px', borderRadius: 6, border: `1px solid ${STATUS_COLORS[s]}44`,
                        background: c.status === s ? STATUS_COLORS[s] + '22' : 'transparent',
                        color: STATUS_COLORS[s], fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      }}>{s}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
